import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Search, X, ArrowRight } from "lucide-react";
import { navItems } from "../mock";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { useI18n } from "../i18n/I18nContext";
import LanguageSwitcher from "./LanguageSwitcher";

const Logo = ({ light }) => (
  <Link to="/" className="flex items-center gap-2 group">
    <div className="flex items-center justify-center w-9 h-9 rounded-md bg-[#0a2540] text-white font-display font-800 text-lg leading-none">
      B
    </div>
    <span
      className={`font-display font-800 text-xl tracking-tight ${
        light ? "text-white" : "text-[#0a2540]"
      }`}
    >
      BPC
    </span>
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useI18n();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 border-b ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-slate-200 shadow-sm"
          : "bg-white border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Logo />

          <nav className="hidden lg:flex items-center gap-9">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`link-underline text-[15px] font-500 transition-colors ${
                  location.pathname === item.path
                    ? "text-[#0a6ed1]"
                    : "text-[#0a2540] hover:text-[#0a6ed1]"
                }`}
              >
                {t(item.label.toLowerCase())}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <button className="hidden md:flex items-center justify-center w-10 h-10 rounded-full hover:bg-slate-100 transition-colors">
              <Search className="w-5 h-5 text-[#0a2540]" />
            </button>
            <Link
              to="/contact"
              className="hidden md:inline-flex items-center gap-2 bg-[#0a2540] hover:bg-[#16355c] text-white text-sm font-600 px-5 py-2.5 rounded-full transition-colors"
            >
              {t("getInTouch")} <ArrowRight className="w-4 h-4" />
            </Link>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full hover:bg-slate-100 transition-colors">
                  <Menu className="w-6 h-6 text-[#0a2540]" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] p-0">
                <div className="flex items-center justify-between p-5 border-b">
                  <Logo />
                  <button onClick={() => setOpen(false)}>
                    <X className="w-6 h-6 text-[#0a2540]" />
                  </button>
                </div>
                <nav className="flex flex-col p-5 gap-1">
                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setOpen(false)}
                      className="flex items-center justify-between py-3 text-lg font-600 text-[#0a2540] border-b border-slate-100"
                    >
                      {t(item.label.toLowerCase())}
                      <ArrowRight className="w-4 h-4 text-[#0a6ed1]" />
                    </Link>
                  ))}
                  <Link
                    to="/contact"
                    onClick={() => setOpen(false)}
                    className="mt-5 inline-flex items-center justify-center gap-2 bg-[#0a2540] text-white font-600 px-5 py-3 rounded-full"
                  >
                    {t("getInTouch")} <ArrowRight className="w-4 h-4" />
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
